import {emptyObject} from '@packages/utilities/fp/utils';
import {
  Component,
  ExpressionPath,
  ExpressionPathPart,
  Expressions,
  Plugins,
} from '@packages/systems/wfdl';
import {
  ExpressionElement,
  Expression,
  EElement,
  EList,
} from '@packages/systems/wfdl-ast';
import {
  findElement,
  getChildren,
  getPageComponentRender,
  getPageComponentRootElement,
  updateElementInComponent,
  updateElementChildren,
  isUserComponentInst,
  getComponentInstRootElement,
  type TraversalDirection,
} from '@packages/utilities/platform';
import {updateElementData} from '@packages/utilities/platform-elementdata';
import {
  isFrameElement,
  isFrameLayoutElement,
} from '@packages/utilities/elementTypeUtils';
import {
  BASE_VARIANT_DEFAULTS,
  getBaseVariantDisplayName,
} from '@packages/systems/component-style-variants';
import type {BreakpointID} from '@packages/systems/style/types';
import {
  FrameWidth,
  FrameSettingKey,
  FrameSettingOverride,
  FrameSettingOverrideMap,
  RenderedElementSnapshot,
  BindableColorValue,
} from '@packages/systems/component-canvas/types';
import {
  VariantStateKey,
  stringifyVariantStateKey,
} from '@packages/systems/style-legacy/selectors';
import {qualify} from '@packages/systems/wfdl-Qualified';
import {NativeId} from '@packages/systems/core/utils/NativeIdUtils';
import {
  DEFAULT_BREAKPOINTS_CONFIG,
  LARGER_BREAKPOINTS_CONFIG,
} from '@packages/systems/style/breakpoints-config';
import {
  DEFAULT_BREAKPOINT_IDS,
  LARGER_BREAKPOINT_IDS,
  BREAKPOINT_ID_MAIN,
} from '@packages/systems/style/breakpoint-ids';
import {DEFAULT_VALUES as MIN_BREAKPOINT_VALUES} from '@packages/systems/style-legacy/media-queries';

const inlineDisplays = ['inline', 'inline-block', 'inline-flex', 'inline-grid'];

/**
 * Strategy function that determines the base variant ID from a list of
 * variant options. Different component types use different conventions:
 * - Native components always have a variant with `id: 'base'`
 * - Code components prefer a variant whose id is 'base' (case-insensitive),
 *   falling back to the first option
 */
export type GetBaseVariantId = (
  variantOptions: Component.StyleVariant[]
) => string | undefined;

/**
 * Native component strategy: the base variant always has `id: 'base'`.
 */
export const getBaseVariantIdForNativeComponent: GetBaseVariantId = () =>
  BASE_VARIANT_DEFAULTS.id;

/**
 * Code component strategy: prefer a variant whose id is 'base' (case-insensitive),
 * otherwise fall back to the first variant option.
 */
export const getBaseVariantIdForCodeComponent: GetBaseVariantId = (
  variantOptions
) =>
  variantOptions.find((v) => v.id.toLowerCase() === BASE_VARIANT_DEFAULTS.id)
    ?.id ?? variantOptions[0]?.id;

export function isElementBaseFrame(
  element: ExpressionElement,
  componentMap: Component.ComponentMap,
  getBaseVariantId: GetBaseVariantId = getBaseVariantIdForNativeComponent
): boolean {
  if (!isFrameElement(element)) {
    return false;
  }

  const childElement = getVariantInstanceElementFromFrame(
    element,
    componentMap
  );
  if (!childElement) {
    return false;
  }

  const childComponent = componentMap.get(childElement.type);
  const variants = childComponent
    ? Component.getVariants(childComponent)
    : null;

  // If there are no variants then we should have only one Frame which is the base frame
  if (!variants || Object.keys(variants).length < 1) {
    return true;
  }

  // If there are variants check if the current instance has the base variant selected
  const variantPropId = Object.keys(variants)[0];
  if (!variantPropId) return false;

  const variantOptions = variants[variantPropId];
  const baseVariantId = variantOptions
    ? getBaseVariantId(variantOptions)
    : undefined;
  if (!baseVariantId) return false;

  const variantPropData = Expressions.getIn(
    Expressions.getElementData(childElement),
    [{in: 'Record', at: variantPropId}]
  );
  const instanceVariantId = variantPropData
    ? Expressions.getLiteral(variantPropData)
    : null;

  // If there's no variant override data, consider it a base frame.
  // This is the case when the component is updated from no variants to having variants and we haven't synced the Frames yet
  if (!instanceVariantId) return true;

  return instanceVariantId.value === baseVariantId;
}

/**
 * Given a component map, returns the Frame Element of the base variant.
 * It is assumed the `componentMap` is of a Component Page
 */
export function getBaseVariantFrame(
  componentMap: Component.ComponentMap,
  getBaseVariantId: GetBaseVariantId = getBaseVariantIdForNativeComponent
): ExpressionElement | null {
  const rootExpr = getPageComponentRender(componentMap);
  const rootElement = rootExpr ? Expressions.getElement(rootExpr) : null;

  if (!rootElement) return null;

  const firstFrame = findElement(rootElement, (element) => {
    return isElementBaseFrame(element, componentMap, getBaseVariantId);
  });
  return firstFrame;
}

/**
 * Given an id, returns the Frame element matching the id
 */
export function getFrameById(
  id: string,
  componentMap: Component.ComponentMap
): ExpressionElement | null {
  const rootExpr = getPageComponentRender(componentMap);

  const maybeFrame = rootExpr && Expressions.getElementById(rootExpr, id);
  return maybeFrame && isFrameElement(maybeFrame) ? maybeFrame : null;
}

/**
 * Given a Frame element, returns the User Component instance of the variant
 * hosted by the given frame.
 * @param element
 */
export function getVariantInstanceElementFromFrame(
  frame: ExpressionElement,
  componentMap: Component.ComponentMap
): ExpressionElement | null {
  const children = getChildren(frame);
  const childElement = children && children[0];

  if (!childElement || !isUserComponentInst(componentMap, childElement)) {
    return null;
  }

  return childElement;
}

const breakpointsDown =
  DEFAULT_BREAKPOINT_IDS as unknown as Array<BreakpointID>;
const breakpointsUp = ([BREAKPOINT_ID_MAIN] as Array<BreakpointID>).concat(
  LARGER_BREAKPOINT_IDS
);

/**
 * Breakpoint groups for cascade inheritance.
 * - 'main': The pivot breakpoint shared by both cascades
 * - 'larger': Breakpoints using minWidth queries (large, xl, xxl)
 * - 'smaller': Breakpoints using maxWidth queries (medium, small, tiny)
 */
type BreakpointGroup = 'main' | 'larger' | 'smaller';

/**
 * Determines which cascade group a breakpoint belongs to.
 * This is critical for ensuring settings don't incorrectly inherit across
 * the "larger" and "smaller" breakpoint groups which use different CSS
 * media query strategies (minWidth vs maxWidth).
 */
const getBreakpointGroup = (mq: BreakpointID): BreakpointGroup => {
  if (mq === BREAKPOINT_ID_MAIN) return 'main';

  // Our default breakpoints consist of the `main` and smaller breakpoints
  // The `main` case is explicitly handled above. If `mq` is otherwise found
  //  within this config, it matches one of the "smaller" breakpoints
  if (DEFAULT_BREAKPOINTS_CONFIG[mq]) return 'smaller';

  // Otherwise assume the breakpoint falls in the "larger" group
  return 'larger';
};

const atSize: ExpressionPathPart = {in: 'Record', at: 'size'};
const atBackground: ExpressionPathPart = {in: 'Record', at: 'background'};

const path = (
  mq: BreakpointID,
  setting: 'width' | 'height' | 'color'
): ExpressionPath => [
  setting === 'color' ? atBackground : atSize,
  {in: 'Record', at: mq},
  {in: 'Record', at: setting},
];

export const getOverrides = (
  element: ExpressionElement
): Partial<FrameSettingOverrideMap> =>
  (element.meta?.frameSettingOverrides as
    | FrameSettingOverrideMap
    | undefined) ?? emptyObject;

const findInheritanceSourceFromBreakpoints =
  (
    overrides: Partial<FrameSettingOverrideMap>,
    currentMediaQuery: BreakpointID,
    setting: 'width' | 'height' | 'color',
    includeCurrentMediaQuery: boolean
  ) =>
  (breakpoints: Array<BreakpointID>) => {
    let result;

    for (const mq of breakpoints) {
      const hasOverride = overrides[mq]?.[setting];
      if (hasOverride && (mq !== currentMediaQuery || includeCurrentMediaQuery))
        result = mq;
      if (mq === currentMediaQuery) break;
    }

    return result;
  };

/**
 * Finds the breakpoint from which the current breakpoint should inherit a setting.
 * Only searches within the applicable breakpoint group to ensure "larger" and
 * "smaller" breakpoints don't incorrectly inherit from each other.
 *
 * @param overrides - The override map for the frame
 * @param currentMediaQuery - The current breakpoint being queried
 * @param setting - The setting type (width, height, or color)
 * @param includeCurrentMediaQuery - Whether to include the current breakpoint as a potential source
 * @returns The breakpoint ID to inherit from, or undefined if none found
 */
export const findInheritanceSource = (
  overrides: Partial<FrameSettingOverrideMap>,
  currentMediaQuery: BreakpointID,
  setting: 'width' | 'height' | 'color',
  includeCurrentMediaQuery: boolean
): BreakpointID | undefined => {
  const find = findInheritanceSourceFromBreakpoints(
    overrides,
    currentMediaQuery,
    setting,
    includeCurrentMediaQuery
  );

  const group = getBreakpointGroup(currentMediaQuery);

  // Only search within the breakpoint group that contains currentMediaQuery
  // This ensures "larger" and "smaller" breakpoints don't cross-inherit
  switch (group) {
    case 'larger':
      return find(breakpointsUp);
    case 'smaller':
      return find(breakpointsDown);
    case 'main':
      // Main is the root - check if it has an override when including current
      return includeCurrentMediaQuery ? find(breakpointsDown) : undefined;
  }
};

const breakpointInheritance =
  (
    expr: Expression,
    currentMediaQuery: BreakpointID,
    overrides: Partial<FrameSettingOverrideMap>,
    setting: 'width' | 'height' | 'color'
  ) =>
  (data: Expression, breakpoints: Array<BreakpointID>) => {
    let result = data;
    let found = false;
    for (const mq of breakpoints) {
      if (found) {
        const hasOverride = overrides[mq]?.[setting];
        if (hasOverride) break;
      }
      if (mq === currentMediaQuery) found = true;
      if (found)
        result = Expressions.sparseSetIn(result, path(mq, setting), expr);
    }
    return result;
  };

const baseFrameInheritance =
  (
    expr: Expression,
    overrides: Partial<FrameSettingOverrideMap>,
    baseOverrides: Partial<FrameSettingOverrideMap>,
    currentMediaQuery: BreakpointID,
    setting: 'width' | 'height' | 'color'
  ) =>
  (data: Expression, breakpoints: Array<BreakpointID>) => {
    let result = data;
    let found = false;
    for (const mq of breakpoints) {
      const hasOverride = overrides[mq]?.[setting];
      if (hasOverride) break;
      if (mq === currentMediaQuery) found = true;
      if (found && mq !== currentMediaQuery) {
        // stop propagating if the base has its own override at this more-specific breakpoint
        const hasBaseOverride = baseOverrides[mq]?.[setting];
        if (hasBaseOverride) break;
      }
      if (found)
        result = Expressions.sparseSetIn(result, path(mq, setting), expr);
    }
    return result;
  };

/**
 * Applies an update function to the appropriate breakpoint groups based on
 * the provided breakpointId. Main cascades to both groups, while larger/smaller
 * only cascade within their respective group.
 */
const applyToBreakpointGroups = (
  data: Expression,
  breakpointId: BreakpointID,
  updateFn: (data: Expression, breakpoints: Array<BreakpointID>) => Expression
): Expression => {
  const group = getBreakpointGroup(breakpointId);
  let result = data;
  if (group === 'main' || group === 'larger') {
    result = updateFn(result, breakpointsUp);
  }
  if (group === 'main' || group === 'smaller') {
    result = updateFn(result, breakpointsDown);
  }
  return result;
};

/**
 * Applies a setting change to element data within the appropriate breakpoint
 * cascade and marks the setting as overridden in metadata.
 *
 * The cascade is restricted to the breakpoint group containing the mediaQuery:
 * - 'main' affects both larger and smaller breakpoints (it's the pivot)
 * - 'larger' (large, xl, xxl) only affects other larger breakpoints
 * - 'smaller' (medium, small, tiny) only affects other smaller breakpoints
 */
function applyFrameSettingChange(
  element: ExpressionElement,
  mediaQuery: BreakpointID,
  setting: 'width' | 'height' | 'color',
  expr: Expression
): ExpressionElement {
  const existingOverrides = getOverrides(element);

  const updatedElement = updateElementData((elementData) => {
    const updateBreakpoints = breakpointInheritance(
      expr,
      mediaQuery,
      existingOverrides,
      setting
    );
    return applyToBreakpointGroups(elementData, mediaQuery, updateBreakpoints);
  })(element);

  const existingBreakpointOverrides: FrameSettingOverride =
    existingOverrides[mediaQuery] ?? {};

  // Mark frame as overridden for this breakpoint and setting to prevent base frame inheritance
  const updatedOverrides: Partial<FrameSettingOverrideMap> = {
    ...existingOverrides,
    [mediaQuery]: {
      ...existingBreakpointOverrides,
      [setting]: true,
    },
  };

  return {
    ...updatedElement,
    meta: {
      ...element.meta,
      frameSettingOverrides: updatedOverrides,
    },
  };
}

/**
 * Updates the base frame and propagates the setting to variant frames that
 * should inherit the value. Inheritance only propagates within the appropriate
 * breakpoint cascade group.
 */
function updateBaseWithVariants(
  componentMap: Component.ComponentMap,
  baseFrame: ExpressionElement,
  pageRender: Expression,
  mediaQuery: BreakpointID,
  setting: 'width' | 'height' | 'color',
  expr: Expression
): Component.ComponentMap {
  const pageRenderElement = Expressions.getElement(pageRender);
  if (!pageRenderElement) return componentMap;

  const frameLayoutElement = findElement(
    pageRenderElement,
    isFrameLayoutElement
  );
  if (!frameLayoutElement) return componentMap;

  return updateElementInComponent(
    componentMap,
    'page',
    frameLayoutElement.id,
    (el) => {
      return updateElementChildren(el, (children) => {
        const childrenList = Expressions.getList(children);
        if (!childrenList) return children;

        const updatedChildren = childrenList.map((elExpr) => {
          const frameElement = Expressions.getElement(elExpr);
          if (!frameElement) return elExpr;

          if (frameElement.id === baseFrame.id) return EElement(baseFrame);

          const updatedFrameElement = updateElementData((elementData) => {
            const updateBreakpoints = baseFrameInheritance(
              expr,
              getOverrides(frameElement),
              getOverrides(baseFrame),
              mediaQuery,
              setting
            );
            return applyToBreakpointGroups(
              elementData,
              mediaQuery,
              updateBreakpoints
            );
          })(frameElement);

          return EElement(updatedFrameElement);
        });

        return EList(updatedChildren);
      });
    }
  );
}

/**
 * Updates a frame setting, sets the override flag, and copies the value to all
 * media queries that need to inherit it (including other frames if the base
 * frame is being updated).
 */
export function setFrameSetting(
  componentMap: Component.ComponentMap,
  frameId: string,
  mediaQuery: BreakpointID,
  setting: 'width' | 'height' | 'color',
  expr: Expression,
  getBaseVariantId: GetBaseVariantId = getBaseVariantIdForNativeComponent
): Component.ComponentMap {
  const pageRender = getPageComponentRender(componentMap);
  if (!pageRender) return componentMap;

  const element = Expressions.getElementById(pageRender, frameId);
  if (!element || !isFrameElement(element)) return componentMap;

  const isBaseFrame = isElementBaseFrame(
    element,
    componentMap,
    getBaseVariantId
  );

  // Update frame setting and mark as overridden to prevent future base frame inheritance updates
  const updatedFrameElement = applyFrameSettingChange(
    element,
    mediaQuery,
    setting,
    expr
  );

  if (isBaseFrame) {
    return updateBaseWithVariants(
      componentMap,
      updatedFrameElement,
      pageRender,
      mediaQuery,
      setting,
      expr
    );
  }

  return updateElementInComponent(
    componentMap,
    'page',
    element.id,
    () => updatedFrameElement
  );
}

export const FRAME_DIMENSION_MODE = {
  FIT: 'fit',
  FULL: 'full',
  FIXED: 'fixed',
} as const;

type FrameDimensionMode =
  (typeof FRAME_DIMENSION_MODE)[keyof typeof FRAME_DIMENSION_MODE];

/**
 * Resets a frame setting override to inherit from an appropriate breakpoint
 * from the same frame or the base frame.
 * Copies the setting from the inherited value and clears the override flag.
 *
 * The inheritance cascade is restricted to the breakpoint group containing
 * the mediaQuery to ensure "larger" and "smaller" breakpoints don't cross-inherit.
 */
export function resetFrameSetting(
  componentMap: Component.ComponentMap,
  frameId: string,
  mediaQuery: BreakpointID,
  setting: 'width' | 'height' | 'color',
  defaultValue: Expression,
  getBaseVariantId: GetBaseVariantId = getBaseVariantIdForNativeComponent
): Component.ComponentMap {
  const pageRender = getPageComponentRender(componentMap);
  if (!pageRender) return componentMap;

  const element = Expressions.getElementById(pageRender, frameId);

  if (!element || !isFrameElement(element)) return componentMap;

  // Get the base variant frame
  const baseFrame = getBaseVariantFrame(componentMap, getBaseVariantId);
  if (!baseFrame) return componentMap;

  const overrides = getOverrides(element);

  const variantInheritFrom = findInheritanceSource(
    overrides,
    mediaQuery,
    setting,
    false
  );

  const baseInheritFrom =
    variantInheritFrom || baseFrame.id === frameId
      ? undefined
      : findInheritanceSource(
          getOverrides(baseFrame),
          mediaQuery,
          setting,
          true
        );

  const maybeExpr = variantInheritFrom
    ? Expressions.getIn(
        Expressions.getElementData(element),
        path(variantInheritFrom, setting)
      )
    : baseInheritFrom
      ? Expressions.getIn(
          Expressions.getElementData(baseFrame),
          path(baseInheritFrom, setting)
        )
      : undefined;

  const expr = maybeExpr || defaultValue;

  const updatedFrameElement = updateElementData((elementData) => {
    const updateBreakpoints = breakpointInheritance(
      expr,
      mediaQuery,
      overrides,
      setting
    );
    return applyToBreakpointGroups(elementData, mediaQuery, updateBreakpoints);
  })(element);

  // Clear the override flag
  const frameWithClearedOverride = clearFrameSettingOverride(
    updatedFrameElement,
    mediaQuery,
    setting
  );

  // If we update base frame we need to update all variants to inherit from it
  if (frameId === baseFrame.id)
    return updateBaseWithVariants(
      componentMap,
      frameWithClearedOverride,
      pageRender,
      mediaQuery,
      setting,
      expr
    );

  return updateElementInComponent(
    componentMap,
    'page',
    element.id,
    () => frameWithClearedOverride
  );
}
/**
 * Determines the initial frame width when entering the Component Canvas.
 *
 * The logic follows these rules:
 * 1. Blank code components use "fit" width — they render their own output, so
 *    the frame hugs it rather than opening as wide as the breakpoint
 * 2. Other blank components always use "full" width
 * 3. If the root element uses an inline display, set to "fit"
 * 4. If the rendered width is wider than the `main` breakpoint's minimum, set to "full"
 * 5. Otherwise, set to "fixed" with the rendered width of the element
 */
export const getInitialFrameWidth = (
  isBlankComponent: boolean,
  renderedSnapshot?: RenderedElementSnapshot | null,
  isCodeComponent = false
): FrameWidth => {
  // A blank code component has no source element to measure, but it does render
  // its own markup, so hug that instead of the breakpoint width. This replaces
  // the `cachedFrameWidths` seed that `CODE_COMPONENT_CREATED` used to write —
  // see the note there (EXT-2005) for why that cache must not be spent here.
  if (isBlankComponent && isCodeComponent) {
    return {
      mode: 'fit',
    };
  }

  // Blank components always use full width
  if (isBlankComponent) {
    return {
      mode: 'full',
    };
  }

  const {display, width} = renderedSnapshot ?? {};

  // Use the display value from the snapshot to determine if the element
  // was rendered with an inline display. This captures all CSS sources including
  // bootflow classes, atom styles, user classes, and inline styles.
  // If display is considered inline, frame should have mode: 'fit'
  if (display && inlineDisplays.includes(display)) {
    return {
      mode: 'fit',
    };
  }

  if (width && width > 0) {
    // Rendered width is used to initialize the main breakpoint Frame width,
    // regardless of the breakpoint that was active when the element was measured.
    const mainMinWidth = MIN_BREAKPOINT_VALUES['main'];

    // Use 'full' when the rendered width is at, or above, the main breakpoint minimum
    if (width >= mainMinWidth) {
      return {
        mode: 'full',
      };
    }

    // Otherwise, use 'fixed' with the rendered width
    return {
      mode: 'fixed',
      // We don't allow sub-pixel values for Frame sizes, so the width
      // measured from the DOM needs to be rounded to the nearest integer
      value: Math.round(width),
    };
  }

  // Fallback to 'full' if the source element is not inline display
  // and does not have a non-zero rendered width
  return {
    mode: 'full',
  };
};

/**
 * Returns the variant prop id for a given component instance element.
 */
export function getVariantPropId(
  componentMap: Component.ComponentMap,
  element: ExpressionElement
): string | null {
  if (!isUserComponentInst(componentMap, element)) {
    return null;
  }

  const component = componentMap.get(element.type);
  if (!component) {
    return null;
  }

  const variants = Component.getVariants(component);
  if (!variants || Object.keys(variants).length < 1) {
    return null;
  }

  // Return the first variant prop id
  return Object.keys(variants)[0] || null;
}

/**
 * Given a frame element, returns the variant of the instance element within that frame.
 */
export function getVariantIdFromFrameElement(
  frameElement: ExpressionElement,
  componentMap: Component.ComponentMap
): string | null {
  const instanceElement = getVariantInstanceElementFromFrame(
    frameElement,
    componentMap
  );
  if (!instanceElement) return null;

  const variantPropId = getVariantPropId(componentMap, instanceElement);
  // Return base variant id if component has no variants
  if (!variantPropId) return BASE_VARIANT_DEFAULTS.id;

  const variantData = Expressions.getIn(
    Expressions.getElementData(instanceElement),
    [{in: 'Record', at: variantPropId}]
  );
  if (!variantData) return null;

  const {value} = Expressions.getLiteral(variantData) ?? {};
  return value ? String(value) : null;
}

/**
 * Given a component instance element, returns the display name of the base variant.
 * Returns 'Base' as fallback if the component or variants cannot be found.
 */
export function getBaseVariantName(
  instanceElement: ExpressionElement,
  componentMap: Component.ComponentMap
): string {
  const component = componentMap.get(instanceElement.type);
  if (!component) return BASE_VARIANT_DEFAULTS.displayName;

  return getBaseVariantDisplayName(component);
}

export const generateVariantStateKeyForFrame = (
  frame: ExpressionElement,
  components: Component.ComponentMap
) => {
  const inst = getVariantInstanceElementFromFrame(frame, components);
  if (!inst) return null;

  const variantPropId = getVariantPropId(components, inst);
  if (!variantPropId) return null;

  const variantOptionId = getVariantIdFromFrameElement(frame, components);
  if (!variantOptionId) return null;

  return stringifyVariantStateKey(variantPropId, variantOptionId);
};

/**
 * Returns the Base variant option for a component where it's name is either:
 *   - From the Base variant's `displayName`, if a Base variant exists
 *   - From component.placeholderBaseVariantName, if the component does not have
 *     any style variants.
 */
const getDefaultBaseVariant = (
  component: Component.ComponentType
): {id: typeof BASE_VARIANT_DEFAULTS.id; displayName: string} => {
  const displayName = getBaseVariantDisplayName(component);

  return {
    id: BASE_VARIANT_DEFAULTS.id,
    displayName,
  };
};

/**
 * Returns the component variant associated with a given frame ID.
 */
export const getVariantAssociatedWithFrame = (
  frameId: string,
  componentMap: Component.ComponentMap
) => {
  const frame = getFrameById(frameId, componentMap);
  if (!frame) return null;

  const instanceElement = getVariantInstanceElementFromFrame(
    frame,
    componentMap
  );

  if (!instanceElement) return null;

  const component = componentMap.get(
    qualify(
      Plugins.name('__SitePlugin'),
      Component.name(instanceElement.type[1])
    )
  );

  if (!component) return null;

  const variantFields = Component.getVariants(component);

  // this covers an edge case for newly created components that don't have variants yet
  if (variantFields == null) return getDefaultBaseVariant(component);

  const variantPropId = getVariantPropId(componentMap, instanceElement);
  if (!variantPropId) return getDefaultBaseVariant(component);

  const variants = variantFields[variantPropId];
  if (!variants) return null;

  const variantData = Expressions.getIn(
    Expressions.getElementData(instanceElement),
    [{in: 'Record', at: variantPropId}]
  );
  const selectedId =
    (variantData && Expressions.getLiteral(variantData)?.value) ??
    BASE_VARIANT_DEFAULTS.id;

  return variants.find((v) => v.id === selectedId) ?? null;
};

/**
 * Returns all frame elements from the component map's page component.
 */
export const getAllFrameElementsFromPage = (
  componentMap: Component.ComponentMap
): ExpressionElement[] | null => {
  const pageRenderElement = getPageComponentRootElement(componentMap);
  if (!pageRenderElement) return null;

  const frameLayoutElement = findElement(
    pageRenderElement,
    isFrameLayoutElement
  );
  if (!frameLayoutElement) return null;

  // FrameLayout can only have Frame elements as children
  const children = getChildren(frameLayoutElement);

  return children?.length ? children : null;
};

/**
 * Returns the Frame element matching the provided variant ID.
 * If no frame is found, return null.
 */
export const getFrameElementFromVariantId = (
  variantId: string,
  componentMap: Component.ComponentMap,
  getBaseVariantId: GetBaseVariantId = getBaseVariantIdForNativeComponent
): ExpressionElement | null => {
  const frames = getAllFrameElementsFromPage(componentMap);
  if (!frames) return null;

  for (const frame of frames) {
    if (
      variantId === BASE_VARIANT_DEFAULTS.id &&
      isElementBaseFrame(frame, componentMap, getBaseVariantId)
    ) {
      return frame;
    }
    const frameVariantId = getVariantIdFromFrameElement(frame, componentMap);
    if (frameVariantId === variantId) {
      return frame;
    }
  }

  return null;
};

/**
 * Returns the currently active frame element by looking at the focused symbol path.
 */
export const getActiveFrameElement = (
  componentMap: Component.ComponentMap,
  focusedSymbolNativeIdPath: NativeId[]
) => {
  if (focusedSymbolNativeIdPath && focusedSymbolNativeIdPath.length > 0) {
    const pageRenderExpr = getPageComponentRender(componentMap);

    if (!pageRenderExpr) return;

    const frameElement = Expressions.getParent(
      pageRenderExpr,
      focusedSymbolNativeIdPath[0]!
    );

    if (!frameElement) return null;

    return frameElement;
  }
  return null;
};

/**
 * Gets the next frame element in the ordered frame list.
 * Returns null if the current frame is the last one.
 */
export const getNextFrameElement = (
  currentFrameId: string,
  componentMap: Component.ComponentMap
): ExpressionElement | null => {
  const frames = getAllFrameElementsFromPage(componentMap);
  if (!frames || frames.length === 0) return null;

  const currentIndex = frames.findIndex((frame) => frame.id === currentFrameId);
  if (currentIndex === -1) return null;

  return frames[currentIndex + 1] ?? null;
};

/**
 * Gets the previous frame element in the ordered frame list.
 * Returns null if the current frame is the first one.
 */
export const getPreviousFrameElement = (
  currentFrameId: string,
  componentMap: Component.ComponentMap
): ExpressionElement | null => {
  const frames = getAllFrameElementsFromPage(componentMap);
  if (!frames || frames.length === 0) return null;

  const currentIndex = frames.findIndex((frame) => frame.id === currentFrameId);
  if (currentIndex === -1) return null;

  return frames[currentIndex - 1] ?? null;
};

/**
 * Gets the root element of the component instance hosted within a frame.
 */
export const getVariantRootElementFromFrame = (
  frame: ExpressionElement,
  componentMap: Component.ComponentMap
): ExpressionElement | null => {
  const variantInstance = getVariantInstanceElementFromFrame(
    frame,
    componentMap
  );
  if (!variantInstance) return null;

  const rootElement =
    getComponentInstRootElement(componentMap)(variantInstance);
  return rootElement ?? null;
};

export type ComponentCanvasTraversal =
  | {type: 'frame'; frameId: string}
  | {type: 'element'; nativeIdPath: NativeId[]}
  | {type: 'none'};

export type ComponentCanvasTraversalArgs = {
  direction?: TraversalDirection;
  isVariantRootSelected: boolean;
  activeFrameId?: string | null;
  selectedFrameId?: string | null;
  focusedSymbolNativeIdPath: NativeId[];
  componentMap: Component.ComponentMap;
};

/**
 * Determines the next selection target when navigating the Component Canvas with keyboard arrows.
 *
 * Navigation behavior:
 * - Up: When the variant's root element is selected, navigates back up to select the frame itself
 * - Down: When a frame is selected, navigates into it to select the variant's root element
 * - Left/Right: Navigates between sibling frames in the frame list order
 */
export const traverseComponentCanvasSelection = ({
  direction,
  isVariantRootSelected,
  activeFrameId,
  selectedFrameId,
  focusedSymbolNativeIdPath,
  componentMap,
}: ComponentCanvasTraversalArgs): ComponentCanvasTraversal => {
  // No direction provided, nothing to traverse
  if (!direction) {
    return {type: 'none'};
  }

  // When the variant root element is selected and user presses up,
  // navigate back to select the parent frame
  if (isVariantRootSelected && direction === 'up') {
    return activeFrameId
      ? {type: 'frame', frameId: activeFrameId}
      : {type: 'none'};
  }

  // No frame is selected - traversal is inside the component element tree,
  // which should be handled by normal element navigation
  if (!selectedFrameId) {
    return {type: 'none'};
  }

  switch (direction) {
    // Down: Navigate from frame selection into the variant's root element
    case 'down': {
      const activeFrame = getActiveFrameElement(
        componentMap,
        focusedSymbolNativeIdPath
      );
      const rootElement =
        activeFrame &&
        getVariantRootElementFromFrame(activeFrame, componentMap);

      if (!rootElement) {
        return {type: 'none'};
      }

      // Navigate to the variant's root element
      return {
        type: 'element',
        nativeIdPath: [...focusedSymbolNativeIdPath, rootElement.id],
      };
    }
    // Right: Navigate to the next frame in the frame list
    case 'right': {
      const nextFrame = getNextFrameElement(selectedFrameId, componentMap);
      return nextFrame
        ? {type: 'frame', frameId: nextFrame.id}
        : {type: 'none'};
    }
    // Left: Navigate to the previous frame in the frame list
    case 'left': {
      const prevFrame = getPreviousFrameElement(selectedFrameId, componentMap);
      return prevFrame
        ? {type: 'frame', frameId: prevFrame.id}
        : {type: 'none'};
    }
    default:
      return {type: 'none'};
  }
};

/**
 * Clears the override flag for a specific frame setting (width, height, or color)
 * at a given breakpoint. Returns the updated element with modified metadata.
 */
export function clearFrameSettingOverride(
  element: ExpressionElement,
  breakpointId: BreakpointID,
  setting: FrameSettingKey
): ExpressionElement {
  const existingOverrides: Partial<FrameSettingOverrideMap> =
    (element.meta?.frameSettingOverrides as
      | FrameSettingOverrideMap
      | undefined) ?? {};
  const breakpointOverride = existingOverrides[breakpointId];

  // Early return if no overrides are found to reduce nesting and avoid
  // spreading `existingOverrides` if the condition is met (not expected)
  if (!breakpointOverride) {
    return element;
  }

  const {[setting]: _s, ...remainingOverrides} = breakpointOverride;

  const updatedOverrides =
    Object.keys(remainingOverrides).length === 0
      ? // Removes the empty object at `breakpointId` from `existingOverrides`
        (({[breakpointId]: _b, ...rest}) => rest)(existingOverrides)
      : // Otherwise combines `existingOverrides` with remaining overrides for `breakpointId`
        {...existingOverrides, [breakpointId]: remainingOverrides};

  return {
    ...element,
    meta: {
      ...element.meta,
      frameSettingOverrides:
        Object.keys(updatedOverrides).length === 0
          ? undefined
          : updatedOverrides,
    },
  };
}

type FrameAndVariantPair = {
  frameElement: ExpressionElement | null;
  variantStateKey: VariantStateKey | null;
};

/**
 * Given a `componentMap` and the `focusedSymbolNativeIdPath`, returns a pair of a Frame element
 * and its corresponding `VariantStateKey` which should be set as active.
 *
 * This function is used in both `ExternalStore` (where we handle the active variant) and `UiNodeStore` (where we handle node selection),
 * to consistently set both values in separate stores.
 * NOTE: This function must be called when the Component Canvas is active (where Frames are available), and
 * when NOT editing in context.
 *
 * @param selectedFrameElementId Optional fallback frame ID from ComponentCanvasStore.
 *   Used for VCC code components where `focusedSymbolNativeIdPath` is empty.
 */
export const getFrameAndVariantPair = (
  componentMap: Component.ComponentMap,
  focusedSymbolNativeIdPath: NativeId[],
  selectedFrameElementId?: string | null,
  getBaseVariantId: GetBaseVariantId = getBaseVariantIdForNativeComponent
): FrameAndVariantPair => {
  const activeFrame =
    getActiveFrameElement(componentMap, focusedSymbolNativeIdPath) ??
    (selectedFrameElementId
      ? getFrameById(selectedFrameElementId, componentMap)
      : null);

  if (!activeFrame) {
    return {
      frameElement: null,
      variantStateKey: null,
    };
  }

  const variantStateKey = generateVariantStateKeyForFrame(
    activeFrame,
    componentMap
  );

  if (variantStateKey === null) {
    const baseFrame = getBaseVariantFrame(componentMap, getBaseVariantId);
    return {
      frameElement: baseFrame ?? null,
      variantStateKey: null,
    };
  }

  return {
    frameElement: activeFrame,
    variantStateKey,
  };
};

/**
 * Minimum frame width in pixels.
 */
export const MIN_FRAME_WIDTH_PX = 16;

/**
 * Given a breakpoint ID, returns the minimum and maximum width for frames at that breakpoint.
 * Minimum width is always 16px for fixed/fit frame modes, regardless of breakpoint.
 * Maximum width is determined by the breakpoint's maxWidth constraint.
 */
export const getMinMaxFrameWidthForBreakpoint = (
  breakpointId: BreakpointID
) => {
  const mqConfig = LARGER_BREAKPOINTS_CONFIG[breakpointId];
  return {
    minWidth: MIN_FRAME_WIDTH_PX,
    maxWidth: mqConfig?.maxWidth || 10000,
  };
};

export {getMinMaxFrameHeight} from '@packages/systems/open-canvas/core';

/**
 * Given a frame element, media query, and dimension, returns the number value for that dimension at that breakpoint.
 * If the underlying value expression is not a number, returns null.
 * @param frameElement
 * @param mediaQuery
 * @param dimension
 * @returns `number | null`
 */
export const getFrameDimensionValue = (
  frameElement: ExpressionElement,
  mediaQuery: BreakpointID,
  dimension: 'width' | 'height'
): number | null => {
  const valueExpressionPath: ExpressionPath = [
    {in: 'Record', at: 'size'},
    {in: 'Record', at: mediaQuery},
    {in: 'Record', at: dimension},
    {in: 'Record', at: 'value'},
  ];

  const valueExpr = Expressions.getIn(
    Expressions.getElementData(frameElement),
    valueExpressionPath
  );

  return valueExpr ? Expressions.getNumber(valueExpr) : null;
};

/**
 * Given a frame element, media query, and dimension, returns whether the frame has a fixed dimension at that breakpoint.
 * @param frameElement
 * @param mediaQuery
 * @param dimension
 * @returns `true` if the frame has a fixed dimension at the given breakpoint, `false` otherwise.
 */
export const isFrameDimensionFixed = (
  frameElement: ExpressionElement,
  mediaQuery: BreakpointID,
  dimension: 'width' | 'height'
): boolean => {
  const mode = getFrameDimensionMode(frameElement, mediaQuery, dimension);
  return mode === 'fixed';
};

/**
 * Given a frame element, media query, and dimension, returns the mode for that dimension at that breakpoint.
 * @param frameElement
 * @param mediaQuery
 * @param dimension
 * @returns `FrameDimensionMode | null`
 */
export const getFrameDimensionMode = (
  frameElement: ExpressionElement,
  mediaQuery: BreakpointID,
  dimension: 'width' | 'height'
): FrameDimensionMode | null => {
  const modePath: ExpressionPath = [
    {in: 'Record', at: 'size'},
    {in: 'Record', at: mediaQuery},
    {in: 'Record', at: dimension},
    {in: 'Record', at: 'mode'},
  ];

  const dimensionExpr = Expressions.getIn(
    Expressions.getElementData(frameElement),
    modePath
  );
  if (!dimensionExpr) return null;

  const mode = Expressions.getUnion(dimensionExpr)?.tag as FrameDimensionMode;
  return mode || null;
};

/**
 * Given a frame element and media query, returns the background color value for that frame at that breakpoint.
 */
export const getFrameBackgroundColorValue = (
  frameElement: ExpressionElement,
  mediaQuery: BreakpointID
): BindableColorValue | null => {
  const bgColorPath: ExpressionPath = [
    {in: 'Record', at: 'background'},
    {in: 'Record', at: mediaQuery},
    {in: 'Record', at: 'color'},
  ];

  const bgColorExpr = Expressions.getIn(
    Expressions.getElementData(frameElement),
    bgColorPath
  );
  if (!bgColorExpr) return null;

  const valLiteral = Expressions.getLiteral(bgColorExpr);
  const val: BindableColorValue | null = valLiteral
    ? (valLiteral.value as BindableColorValue)
    : null;
  return val;
};

/**
 * Given the current dimension mode, returns the next mode to use when quick switching.
 * This is used, for example, when the user double clicks the frame resize handle.
 *
 * The modes are toggled as follows:
 *
 * For Width:
 * Fixed -> Fit
 * Fit -> Full
 * Full -> Fit
 *
 * For height (Does not have "Full"):
 * Fixed -> Fit,
 * Fit -> Fit,
 */
export const getNextToggledFrameDimensionMode = (
  dimension: 'width' | 'height',
  currentMode: FrameDimensionMode
): 'fit' | 'full' | null => {
  const mapCurrentToNext = {
    width: {
      fixed: 'fit',
      fit: 'full',
      full: 'fit',
    },
    height: {
      fixed: 'fit',
      fit: null,
      full: null,
    },
  } as const;

  const nextMode = mapCurrentToNext[dimension][currentMode];
  return nextMode || null;
};

export const forTestSuite = {
  getVariantIdFromFrameElement,
  getAllFrameElementsFromPage,
  getVariantPropId,
  getBaseVariantName,
  getNextToggledFrameDimensionMode,
  getNextFrameElement,
  getPreviousFrameElement,
  getVariantRootElementFromFrame,
};
